import logger from './logger'

interface CachedImage {
  data: string // base64 encoded image
  timestamp: number
  url: string
}

const CACHE_PREFIX = 'img_cache_'
const CACHE_EXPIRY_DAYS = 7 // Cache expires after 7 days

/**
 * Convert image URL to base64 data URL
 */
async function urlToBase64(url: string): Promise<string> {
  try {
    const response = await fetch(url, {
      mode: 'cors',
      cache: 'no-cache',
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`)
    }

    const blob = await response.blob()

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result)
        } else {
          reject(new Error('Failed to convert image to base64'))
        }
      }
      reader.onerror = () => reject(new Error('FileReader error'))
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    logger.error('Failed to convert image to base64:', error)
    throw error
  }
}

/**
 * Generate cache key from user identifier
 */
function getCacheKey(identifier: string): string {
  return `${CACHE_PREFIX}${identifier}`
}

/**
 * Check if cached image is expired
 */
function isCacheExpired(timestamp: number): boolean {
  const now = Date.now()
  const expiryMs = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000
  return now - timestamp > expiryMs
}

/**
 * Cache an image from URL
 * @param identifier - Unique identifier for the image (e.g., userId or email)
 * @param url - Image URL to cache
 * @returns Base64 data URL if successful, null otherwise
 */
export async function cacheImage(identifier: string, url: string): Promise<string | null> {
  try {
    logger.debug(`Caching image for ${identifier}:`, url)

    const base64Data = await urlToBase64(url)

    const cachedImage: CachedImage = {
      data: base64Data,
      timestamp: Date.now(),
      url,
    }

    const cacheKey = getCacheKey(identifier)
    localStorage.setItem(cacheKey, JSON.stringify(cachedImage))

    logger.info(`Image cached successfully for ${identifier}`)
    return base64Data
  } catch (error) {
    logger.error(`Failed to cache image for ${identifier}:`, error)
    return null
  }
}

/**
 * Get cached image
 * @param identifier - Unique identifier for the image
 * @param currentUrl - Current image URL (to check if cache is still valid)
 * @returns Base64 data URL if found and valid, null otherwise
 */
export function getCachedImage(identifier: string, currentUrl?: string): string | null {
  try {
    const cacheKey = getCacheKey(identifier)
    const cached = localStorage.getItem(cacheKey)

    if (!cached) {
      logger.debug(`No cache found for ${identifier}`)
      return null
    }

    const cachedImage: CachedImage = JSON.parse(cached)

    // Check if cache is expired
    if (isCacheExpired(cachedImage.timestamp)) {
      logger.debug(`Cache expired for ${identifier}`)
      localStorage.removeItem(cacheKey)
      return null
    }

    // Check if URL has changed (user changed avatar)
    if (currentUrl && cachedImage.url !== currentUrl) {
      logger.debug(`Cache URL mismatch for ${identifier}, cache will be updated`)
      localStorage.removeItem(cacheKey)
      return null
    }

    logger.debug(`Cache hit for ${identifier}`)
    return cachedImage.data
  } catch (error) {
    logger.error(`Failed to get cached image for ${identifier}:`, error)
    return null
  }
}

/**
 * Clear cached image
 * @param identifier - Unique identifier for the image
 */
export function clearCachedImage(identifier: string): void {
  try {
    const cacheKey = getCacheKey(identifier)
    localStorage.removeItem(cacheKey)
    logger.debug(`Cache cleared for ${identifier}`)
  } catch (error) {
    logger.error(`Failed to clear cache for ${identifier}:`, error)
  }
}

/**
 * Clear all cached images
 */
export function clearAllCachedImages(): void {
  try {
    const keys = Object.keys(localStorage)
    const cacheKeys = keys.filter((key) => key.startsWith(CACHE_PREFIX))

    cacheKeys.forEach((key) => localStorage.removeItem(key))

    logger.info(`Cleared ${cacheKeys.length} cached images`)
  } catch (error) {
    logger.error('Failed to clear all cached images:', error)
  }
}

/**
 * Clean up expired caches
 */
export function cleanupExpiredCaches(): void {
  try {
    const keys = Object.keys(localStorage)
    const cacheKeys = keys.filter((key) => key.startsWith(CACHE_PREFIX))

    let cleanedCount = 0

    cacheKeys.forEach((key) => {
      try {
        const cached = localStorage.getItem(key)
        if (!cached) return

        const cachedImage: CachedImage = JSON.parse(cached)

        if (isCacheExpired(cachedImage.timestamp)) {
          localStorage.removeItem(key)
          cleanedCount++
        }
      } catch (error) {
        // Invalid cache entry, remove it
        localStorage.removeItem(key)
        cleanedCount++
      }
    })

    if (cleanedCount > 0) {
      logger.info(`Cleaned up ${cleanedCount} expired image caches`)
    }
  } catch (error) {
    logger.error('Failed to cleanup expired caches:', error)
  }
}
